import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth, beginGoogleAuth } from "@/lib/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  if (user) return <Navigate to="/my-account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--tmg-ivory)] px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex w-14 h-14 bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] items-center justify-center mb-6">
            <Lock className="w-6 h-6" />
          </div>
          <p className="tmg-overline mb-3">— Admin Access</p>
          <h1 className="font-display text-3xl font-semibold">Sign in to continue</h1>
        </div>
        <form onSubmit={submit} data-testid="admin-login-form" className="bg-white border border-[var(--tmg-line)] p-8 space-y-5">
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            type="button"
            onClick={() => beginGoogleAuth("/admin")}
            data-testid="google-signin-btn"
            className="w-full flex items-center justify-center gap-3 border border-[var(--tmg-line)] hover:border-[var(--tmg-obsidian)] py-3 text-sm font-semibold transition-colors bg-white"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.71v2.25h2.9c1.7-1.56 2.69-3.87 2.69-6.6z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.25c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.95 10.71A5.4 5.4 0 0 1 3.66 9c0-.6.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l2.99-2.33z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.42 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.16 6.65 3.58 9 3.58z"/>
            </svg>
            Sign in with Google
          </button>
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center"><div className="w-full h-px bg-[var(--tmg-line)]" /></div>
            <div className="relative text-center">
              <span className="bg-white px-3 tmg-overline">or use password</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="tmg-overline">Email or Username</Label>
            <Input
              data-testid="admin-email-input"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="tmg-overline">Password</Label>
            <Input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={submitting}
            className="tmg-btn-dark w-full justify-center disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
